import { ASSISTENT_API_URL } from '@/config/backend';
import { useAuthStore } from '@/stores/auth-store';
import { useDevicesStore } from '@/stores/devices-store';
import axios from 'axios';

// Helper functions
const getAuthHeaders = () => useAuthStore.getState().getApiHeaders();
const getCurrentDeviceSerialId = () => useDevicesStore.getState().getCurrentDeviceSerialId() || '';

// Types
export interface Recipient {
    id: string;
    title: string;
    forwardingNumber: string;
    active: boolean;
}

export interface Timeslot {
    id: string;
    receiverId: string;
    dayOfWeek: string;
    startTime: string;
    endTime: string;
}

export interface Profile {
    id: string;
    title: string;
    active: boolean;
    errorhandler: boolean;
    messageActive: string;
    messageInactive: string;
    messageFailed?: string;
    receivers: string[];
    timeslots?: Record<string, Timeslot[]>;
    usedByActions?: string[];
}

// Recipients API
export const getAllRecipients = async (): Promise<Recipient[]> => {
    const url = `${ASSISTENT_API_URL}device/${getCurrentDeviceSerialId()}/callhandling/receivers`;

    try {
        const response = await axios.get(url, { headers: getAuthHeaders() });
        return response.data.resultObject.receivers || [];
    } catch (error) {
        console.error('Failed to fetch recipients:', error);
        throw error;
    }
};

export const createRecipient = async (data: { title: string; forwardingNumber: string }): Promise<any> => {
    const url = `${ASSISTENT_API_URL}device/${getCurrentDeviceSerialId()}/callhandling/receiver`;

    try {
        const response = await axios.post(url, {
            title: data.title,
            forwardingNumber: data.forwardingNumber,
            active: true
        }, { headers: getAuthHeaders() });
        return response.data;
    } catch (error: any) {
        console.error('Failed to create recipient:', error);
        throw error;
    }
};

export const updateRecipient = async (id: string, data: Partial<Recipient>): Promise<any> => {
    const url = `${ASSISTENT_API_URL}device/${getCurrentDeviceSerialId()}/callhandling/receiver/${id}`;

    try {
        const response = await axios.put(url, data, { headers: getAuthHeaders() });
        return response.data;
    } catch (error) {
        console.error('Failed to update recipient:', error);
        throw error;
    }
};

export const deleteRecipient = async (id: string): Promise<any> => {
    const url = `${ASSISTENT_API_URL}device/${getCurrentDeviceSerialId()}/callhandling/receiver/${id}`;

    try {
        const response = await axios.delete(url, { headers: getAuthHeaders() });
        return response.data;
    } catch (error) {
        console.error('Failed to delete recipient:', error);
        throw error;
    }
};

export const updateRecipientStatus = async (id: string, active: boolean): Promise<any> => {
    const url = `${ASSISTENT_API_URL}device/${getCurrentDeviceSerialId()}/callhandling/receiver/${id}/activation/${active}`;

    try {
        const response = await axios.put(url, null, { headers: getAuthHeaders() });
        return response.data;
    } catch (error) {
        console.error('Failed to update recipient status:', error);
        throw error;
    }
};

// Profiles API
export const getAllProfiles = async (): Promise<Profile[]> => {
    const url = `${ASSISTENT_API_URL}device/${getCurrentDeviceSerialId()}/callhandling/profiles`;

    try {
        const response = await axios.get(url, { headers: getAuthHeaders() });
        const profiles = response.data.resultObject.profiles || [];

        // Load full details for each profile to get timeslots
        const detailedProfiles = await Promise.all(
            profiles.map((profile: any) => getProfile(profile.id).catch(() => profile))
        );

        return detailedProfiles;
    } catch (error) {
        console.error('Failed to fetch profiles:', error);
        throw error;
    }
};

export const getProfile = async (profileId: string): Promise<Profile> => {
    const url = `${ASSISTENT_API_URL}device/${getCurrentDeviceSerialId()}/callhandling/profile/${profileId}`;

    try {
        const response = await axios.get(url, { headers: getAuthHeaders() });
        const profileData = response.data.resultObject;

        // Transform schedules array into timeslots grouped by day
        if (profileData.schedules && Array.isArray(profileData.schedules)) {
            const timeslotsByDay: Record<string, Timeslot[]> = {};
            profileData.schedules.forEach((schedule: any) => {
                const day = schedule.dayOfWeek;
                if (!timeslotsByDay[day]) {
                    timeslotsByDay[day] = [];
                }
                timeslotsByDay[day].push({
                    id: schedule.id,
                    receiverId: schedule.receiverId,
                    dayOfWeek: schedule.dayOfWeek,
                    startTime: schedule.startTime,
                    endTime: schedule.endTime
                });
            });
            profileData.timeslots = timeslotsByDay;
        }

        return profileData;
    } catch (error) {
        console.error('Failed to fetch profile:', error);
        throw error;
    }
};

export const createProfile = async (data: {
    title: string;
    receiverId: string;
    messageActive?: string;
    messageInactive?: string;
}): Promise<any> => {
    const url = `${ASSISTENT_API_URL}device/${getCurrentDeviceSerialId()}/callhandling/profile`;

    const defaultActive = "I will try connecting you to our staff now.";
    const defaultInactive = "I'm sorry but our staff is not available right now.";

    try {
        const response = await axios.post(url, {
            title: data.title,
            active: true,
            errorhandler: false,
            messageActive: data.messageActive || defaultActive,
            messageInactive: data.messageInactive || defaultInactive,
            receivers: [data.receiverId]
        }, { headers: getAuthHeaders() });
        return response.data;
    } catch (error) {
        console.error('Failed to create profile:', error);
        throw error;
    }
};

export const updateProfile = async (id: string, data: Partial<Profile>): Promise<any> => {
    const url = `${ASSISTENT_API_URL}device/${getCurrentDeviceSerialId()}/callhandling/profile/${id}`;

    try {
        // Build the update payload
        const payload: any = {};
        if (data.title !== undefined) payload.title = data.title;
        if (data.receivers !== undefined) payload.receivers = data.receivers;
        if (data.messageActive !== undefined) payload.messageActive = data.messageActive;
        if (data.messageInactive !== undefined) payload.messageInactive = data.messageInactive;
        if (data.errorhandler !== undefined) payload.errorhandler = data.errorhandler;
        if (data.active !== undefined) payload.active = data.active;

        const response = await axios.put(url, payload, { headers: getAuthHeaders() });
        return response.data;
    } catch (error) {
        console.error('Failed to update profile:', error);
        throw error;
    }
};

export const updateProfileStatus = async (id: string, active: boolean): Promise<any> => {
    const url = `${ASSISTENT_API_URL}device/${getCurrentDeviceSerialId()}/callhandling/${id}/activation/${active}`;

    try {
        const response = await axios.put(url, null, { headers: getAuthHeaders() });
        return response.data;
    } catch (error) {
        console.error('Failed to update profile status:', error);
        throw error;
    }
};

export const deleteProfile = async (id: string): Promise<any> => {
    const url = `${ASSISTENT_API_URL}device/${getCurrentDeviceSerialId()}/callhandling/profile/${id}`;

    try {
        const response = await axios.delete(url, { headers: getAuthHeaders() });
        return response.data;
    } catch (error) {
        console.error('Failed to delete profile:', error);
        throw error;
    }
};

// Timeslots API
export const createTimeslot = async (profileId: string, data: {
    receiverId: string;
    dayOfWeek: string;
    startTime: string;
    endTime: string;
}): Promise<any> => {
    const url = `${ASSISTENT_API_URL}device/${getCurrentDeviceSerialId()}/callhandling/profile/${profileId}/timeslot`;

    try {
        const response = await axios.post(url, data, { headers: getAuthHeaders() });
        return response.data;
    } catch (error) {
        console.error('Failed to create timeslot:', error);
        throw error;
    }
};

export const deleteTimeslot = async (profileId: string, timeslotId: string): Promise<any> => {
    const url = `${ASSISTENT_API_URL}device/${getCurrentDeviceSerialId()}/callhandling/profile/${profileId}/timeslot/${timeslotId}`;

    try {
        const response = await axios.delete(url, { headers: getAuthHeaders() });
        return response.data;
    } catch (error) {
        console.error('Failed to delete timeslot:', error);
        throw error;
    }
};

// Answer-Forwarding Linking API
export const linkAnswerToProfile = async (answerId: string, profileId: string): Promise<any> => {
    const url = `${ASSISTENT_API_URL}device/${getCurrentDeviceSerialId()}/callhandling/${answerId}/${profileId}`;

    try {
        const response = await axios.put(url, null, { headers: getAuthHeaders() });
        return response.data;
    } catch (error) {
        console.error('Failed to link answer to profile:', error);
        throw error;
    }
};

export const unlinkAnswerFromProfile = async (answerId: string): Promise<any> => {
    const url = `${ASSISTENT_API_URL}device/${getCurrentDeviceSerialId()}/callhandling/${answerId}`;

    try {
        const response = await axios.delete(url, { headers: getAuthHeaders() });
        return response.data;
    } catch (error) {
        console.error('Failed to unlink answer from profile:', error);
        throw error;
    }
};

export const getProfileForAnswer = async (answerId: string): Promise<Profile | null> => {
    // This searches through all profiles to find one that has this answer linked
    try {
        const profiles = await getAllProfiles();
        return profiles.find(p => p.usedByActions?.includes(answerId)) || null;
    } catch (error) {
        console.error('Failed to get profile for answer:', error);
        return null;
    }
};

// SMS Configuration API
export interface SmsConfiguration {
    activeOnAction: string;
    messageToSend: string;
    utteranceToSay: string;
    smsActive: boolean;
}

export const getSmsConfigurations = async (): Promise<SmsConfiguration[]> => {
    const url = `${ASSISTENT_API_URL}device/${getCurrentDeviceSerialId()}/sms/automatic`;

    try {
        const response = await axios.get(url, { headers: getAuthHeaders() });
        return response.data.resultObject?.configurations || [];
    } catch (error) {
        console.error('Failed to get SMS configurations:', error);
        return [];
    }
};

export const updateSmsConfiguration = async (config: SmsConfiguration): Promise<any> => {
    const url = `${ASSISTENT_API_URL}device/${getCurrentDeviceSerialId()}/sms/automatic`;

    try {
        const response = await axios.put(url, config, { headers: getAuthHeaders() });
        return response.data;
    } catch (error) {
        console.error('Failed to update SMS configuration:', error);
        throw error;
    }
};
